import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Badge from "../Badge";
import Card from "../Card";
import DocIcon from "../icons/DocIcon";
import { formatDate, formatPeriod } from "./format";

// Fills of the mock (`.tl-*`): new --tl-new, previously uploaded / in use --tl-old, a replaced report is hatched at 45°
// (2 px line every 6 px) with a 1 px outline, a gap without a report at 135° in --warn with a dashed outline.
// Tailwind needs the full class names in the source, so each fill is spelled out.
const HATCH_OLD = "bg-[image:repeating-linear-gradient(45deg,rgb(var(--tl-old))_0_2px,transparent_2px_6px)] border border-tl-old";
const HATCH_GAP = "bg-[image:repeating-linear-gradient(135deg,rgb(var(--warn))_0_2px,transparent_2px_6px)] border border-dashed border-warn";
const FILL = { new: "bg-tl-new", old: "bg-tl-old", replaced: HATCH_OLD };
const BAR = "absolute inset-y-1 rounded-[4px] ring-2 ring-surface";

const parts = (value) => /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2}))?/.exec(value || "");

// A position in years from 1 January of `firstYear`, read as written (no time zone shift).
function yearPosition(value, firstYear, { endOfDay = false } = {}) {
  const [, y, m, d, hh = "0", mm = "0", ss = "0"] = parts(value) || [];
  const year = Number(y);
  const startOfYear = Date.UTC(year, 0, 1);
  const length = Date.UTC(year + 1, 0, 1) - startOfYear;
  let at = Date.UTC(year, Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
  if (endOfDay && !parts(value)[4]) at += 24 * 3600 * 1000 - 1000;
  return year - firstYear + (at - startOfYear) / length;
}

const yearOf = (value) => Number((parts(value) || [])[1]);
const percent = (value) => `${Math.round(value * 10000) / 100}%`;

// Runs of years inside a report's period that a newer upload took over (not in its parsedYears, AC-13b), clamped to
// the period. A fully replaced report is hatched whole, and without parsedYears there is nothing to tell (OSA-902).
function overlapsOf(report) {
  if (report.replaced || !Array.isArray(report.parsedYears)) return [];
  const used = new Set(report.parsedYears.map(Number));
  const first = yearOf(report.periodStart);
  const last = yearOf(report.periodEnd);
  const runs = [];
  for (let year = first; year <= last; year += 1) {
    if (used.has(year)) continue;
    const end = year === last ? report.periodEnd : `${year}-12-31`;
    const run = runs[runs.length - 1];
    if (run && run.lastYear === year - 1) Object.assign(run, { end, lastYear: year });
    else runs.push({ start: year === first ? report.periodStart : `${year}-01-01`, end, lastYear: year });
  }
  return runs;
}

// Open run: a report just uploaded is «new»; in the confirmed view every report in use is drawn as «old».
export const reportKind = (report, mode = "open") =>
  report.replaced ? "replaced" : mode === "confirmed" || report.linkType !== "UPLOADED" ? "old" : "new";

const MARK = {
  open: { new: ["fresh", "reportTimeline.marks.new"], old: ["muted", "reportTimeline.marks.old"], replaced: ["muted", "reportTimeline.marks.replaced"] },
  confirmed: { old: ["muted", "reportTimeline.marks.used"], replaced: ["muted", "reportTimeline.marks.replaced"] },
};

function Legend({ items }) {
  const { t } = useTranslation();
  return (
    <ul data-testid="timeline-legend" className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-text-muted">
      {items.map(([fill, key]) => (
        <li key={key} className="inline-flex items-center gap-1.5">
          <span data-testid="timeline-swatch" aria-hidden="true" className={clsx("inline-block w-[14px] h-[10px] rounded-[3px]", fill)} />
          <span>{t(key)}</span>
        </li>
      ))}
    </ul>
  );
}

const ROW = "grid grid-cols-1 sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)] gap-3 py-[5px] items-center";

function Track({ grid, children }) {
  return (
    <div data-testid="timeline-track" className="relative h-[22px] rounded-[6px] bg-surface-alt">
      {grid.map((left) => (
        <span key={left} data-testid="timeline-grid" className="absolute -top-0.5 -bottom-0.5 w-px bg-border" style={{ left }} />
      ))}
      {children}
    </div>
  );
}

// SCR-03 / SCR-05 «Звіти»: report periods on one axis of years, gaps named by dates (AC-13, AC-13b, AC-13c, AC-16b).
function ReportTimeline({ reports = [], gaps = [], mode = "open" }) {
  const { t } = useTranslation();
  if (reports.length === 0) return null;
  const confirmed = mode === "confirmed";

  const years = [
    ...reports.flatMap((r) => [yearOf(r.periodStart), yearOf(r.periodEnd)]),
    ...gaps.flatMap((g) => [yearOf(g.start), yearOf(g.end)]),
  ].filter(Number.isFinite);
  const firstYear = Math.min(...years);
  const span = Math.max(...years) - firstYear + 1;
  const axis = Array.from({ length: span }, (_, i) => firstYear + i);
  const grid = axis.slice(1).map((_, i) => percent((i + 1) / span));

  const place = (start, end) => {
    const from = yearPosition(start, firstYear);
    const to = yearPosition(end, firstYear, { endOfDay: true });
    return { left: percent(from / span), width: percent(Math.max(to - from, 0) / span) };
  };

  const kinds = reports.map((report) => reportKind(report, mode));
  const overlaps = reports.map(overlapsOf);
  const legend = [
    !confirmed && kinds.includes("new") && [FILL.new, "reportTimeline.legend.new"],
    kinds.includes("old") && [FILL.old, confirmed ? "reportTimeline.legend.used" : "reportTimeline.legend.old"],
    (kinds.includes("replaced") || overlaps.some((runs) => runs.length > 0)) && [FILL.replaced, "reportTimeline.legend.replaced"],
    gaps.length > 0 && [HATCH_GAP, "reportTimeline.legend.gap"],
  ].filter(Boolean);

  const markOf = (report) => MARK[confirmed ? "confirmed" : "open"][reportKind(report, mode)];

  return (
    <div className="flex flex-col gap-3">
      <Card data-testid="timeline-card" className="p-5">
        <Legend items={legend} />
        <div className="mt-3">
          <div data-testid="timeline-axis" className={ROW}>
            <div className="hidden sm:block" />
            <div className="relative h-4">
              {axis.map((year, i) => (
                <span
                  key={year}
                  data-testid="timeline-year"
                  className="absolute top-0 text-center text-[11px] text-text-muted tabular-nums"
                  style={{ left: percent(i / span), width: percent(1 / span) }}
                >
                  {year}
                </span>
              ))}
            </div>
          </div>
          <div data-testid="timeline-coverage" className={ROW}>
            <div>
              <p className="text-xs font-semibold">{t("reportTimeline.coverage")}</p>
              <p className="text-[11px] text-text-muted">
                {gaps.length > 0 ? t("reportTimeline.gapCount", { n: gaps.length }) : t("reportTimeline.noGaps")}
              </p>
            </div>
            <Track grid={grid}>
              {reports
                .filter((report) => !report.replaced)
                .map((report) => (
                  <span
                    key={report.id}
                    data-testid="timeline-cover"
                    data-tip={t("reportTimeline.tip.cover", { period: formatPeriod(report.periodStart, report.periodEnd) })}
                    className={clsx(BAR, "bg-text-muted/[.55]")}
                    style={place(report.periodStart, report.periodEnd)}
                  />
                ))}
              {gaps.map((gap) => (
                <span
                  key={`${gap.start}-${gap.end}`}
                  data-testid="timeline-gap"
                  data-tip={t("reportTimeline.tip.gap", { period: formatPeriod(gap.start, gap.end) })}
                  className={clsx(BAR, HATCH_GAP)}
                  style={place(gap.start, gap.end)}
                />
              ))}
            </Track>
          </div>
          <div data-testid="timeline-sep" className="h-px bg-border my-1.5" />
          {reports.map((report, i) => {
            const kind = kinds[i];
            const status = t(markOf(report)[1]);
            const period = formatPeriod(report.periodStart, report.periodEnd);
            const meta = t("reportTimeline.meta", { period, date: formatDate(report.createDateTime) });
            return (
              <div key={report.id} data-testid={`timeline-row-${report.fileName}`} className={ROW}>
                <div className="min-w-0">
                  <p className={clsx("text-xs font-medium truncate", kind === "replaced" && "line-through")}>{report.fileName}</p>
                  <p className="text-[11px] text-text-muted">{status}</p>
                </div>
                <Track grid={grid}>
                  <span
                    data-testid="timeline-bar"
                    data-kind={kind}
                    data-tip={`${report.fileName} · ${meta} · ${status}`}
                    className={clsx(BAR, FILL[kind])}
                    style={place(report.periodStart, report.periodEnd)}
                  />
                  {overlaps[i].map((run) => (
                    <span
                      key={run.start}
                      data-testid="timeline-overlap"
                      data-tip={t("reportTimeline.tip.overlap", { period: formatPeriod(run.start, run.end) })}
                      className={clsx(BAR, HATCH_OLD, "bg-surface-alt")}
                      style={place(run.start, run.end)}
                    />
                  ))}
                </Track>
              </div>
            );
          })}
        </div>
        {gaps.length > 0 && (
          <p className="mt-3 text-xs text-warn">
            {t("reportTimeline.gap", { dates: gaps.map((g) => formatPeriod(g.start, g.end)).join("; ") })}
          </p>
        )}
      </Card>
      <ul data-testid="timeline-report-list" className="flex flex-col gap-2">
        {reports.map((report, i) => {
          const [tone, mark] = markOf(report);
          return (
            <li key={report.id} className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-surface-muted">
              <span data-testid="timeline-doc-icon" className="shrink-0">
                <DocIcon />
              </span>
              <div className="min-w-0 flex-1">
                <p className={clsx("text-sm font-medium truncate", kinds[i] === "replaced" && "line-through")}>{report.fileName}</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {t("reportTimeline.meta", {
                    period: formatPeriod(report.periodStart, report.periodEnd),
                    date: formatDate(report.createDateTime),
                  })}
                </p>
              </div>
              <Badge tone={tone}>{t(mark)}</Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ReportTimeline;
